import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Clock,
  CheckCircle2,
  ArrowRight,
  X,
  Loader2,
  Sparkles,
  Award,
} from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEP = {
  INTRO: 'intro',
  READ: 'read',
  QUIZ: 'quiz',
  DONE: 'done',
};

const PlacementTestModal = ({ onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP.INTRO);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bundle, setBundle] = useState(null); // { book, questions }
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);

  const startRef = useRef(null);
  const tickerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  }, []);

  const beginTest = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/placement/test`);
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setBundle(data);
      setAnswers({});
      startRef.current = Date.now();
      setElapsed(0);
      tickerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
      }, 1000);
      setStep(STEP.READ);
    } catch (e) {
      setError(e.message || 'Failed to load test');
    } finally {
      setLoading(false);
    }
  };

  const doneReading = () => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    setStep(STEP.QUIZ);
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        seconds_spent: elapsed || 1,
        word_count: bundle?.book?.word_count || 0,
        answers: (bundle?.questions || []).map((q) =>
          answers[q.id] == null ? null : answers[q.id],
        ),
      };
      const r = await authFetch(`${BACKEND_URL}/api/placement/submit`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(
          typeof data.detail === 'string' ? data.detail : 'Submit failed',
        );
      }
      setResult(data);
      setStep(STEP.DONE);
      if (typeof onComplete === 'function') onComplete(data);
    } catch (e) {
      setError(e.message || 'Submit failed');
    } finally {
      setLoading(false);
    }
  };

  const allAnswered = useMemo(
    () =>
      bundle?.questions?.every((q) => answers[q.id] != null) || false,
    [bundle, answers],
  );

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="placement-modal"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-700 bg-gradient-to-r from-indigo-900/40 to-purple-900/40">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-base">Placement Test</h3>
            <p className="text-xs text-gray-400">
              {step === STEP.INTRO && 'Find your starting level in ~3 minutes'}
              {step === STEP.READ && 'Read naturally, at your own pace'}
              {step === STEP.QUIZ && 'Quick comprehension check'}
              {step === STEP.DONE && 'Your results'}
            </p>
          </div>
          {step === STEP.READ && (
            <div className="flex items-center gap-1.5 text-xs bg-slate-800/70 border border-slate-700 rounded-full px-3 py-1.5 font-mono text-indigo-200">
              <Clock className="w-3 h-3" />
              {mins}:{secs.toString().padStart(2, '0')}
            </div>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800/70 hover:bg-slate-700 text-gray-300 flex items-center justify-center"
            data-testid="placement-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-sm text-red-200">
              {error}
            </div>
          )}

          {step === STEP.INTRO && (
            <div data-testid="placement-intro" className="space-y-5">
              <div className="p-5 rounded-xl bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-pink-500/10 border border-indigo-500/30">
                <p className="text-white leading-relaxed">
                  Take a quick <strong>reading + comprehension</strong> test to
                  find your ideal starting level. We'll measure your reading
                  speed and understanding on a short passage — no prep needed.
                </p>
              </div>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  Read a short excerpt (~300 words)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  Answer 3 quick multiple-choice questions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  Get your level + <strong className="text-amber-300">50 coins</strong> bonus
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  onClick={beginTest}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  data-testid="placement-start"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Take the test <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 bg-transparent border-slate-600 text-gray-300 hover:bg-slate-800"
                  data-testid="placement-skip"
                >
                  Skip for now
                </Button>
              </div>
            </div>
          )}

          {step === STEP.READ && bundle?.book && (
            <div className="space-y-4" data-testid="placement-read">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <BookOpen className="w-4 h-4 text-indigo-300" />
                <span className="font-semibold text-indigo-300">
                  {bundle.book.title}
                </span>
                <span className="text-gray-500">·</span>
                <span>{bundle.book.author}</span>
                <span className="ml-auto text-gray-500">
                  {bundle.book.word_count} words
                </span>
              </div>
              <div className="p-5 rounded-xl bg-slate-800/60 border border-slate-700 text-gray-100 leading-relaxed whitespace-pre-line text-[15px]">
                {bundle.book.text}
              </div>
              <Button
                onClick={doneReading}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="placement-done-reading"
              >
                I'm done reading — continue
              </Button>
            </div>
          )}

          {step === STEP.QUIZ && bundle?.questions && (
            <div className="space-y-4" data-testid="placement-quiz">
              {bundle.questions.map((q, qi) => (
                <div
                  key={q.id}
                  className="p-4 rounded-xl bg-slate-800/60 border border-slate-700"
                >
                  <p className="text-white font-semibold mb-3">
                    {qi + 1}. {q.question}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((opt, i) => {
                      const selected = answers[q.id] === i;
                      return (
                        <label
                          key={i}
                          className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selected
                              ? 'bg-indigo-500/20 border-indigo-400 text-white'
                              : 'bg-slate-900/60 border-slate-700 text-gray-200 hover:border-slate-500'
                          }`}
                          data-testid={`placement-option-${q.id}-${i}`}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            checked={selected}
                            onChange={() =>
                              setAnswers((a) => ({ ...a, [q.id]: i }))
                            }
                            className="accent-indigo-500"
                          />
                          <span className="text-sm">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              <Button
                onClick={submit}
                disabled={!allAnswered || loading}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50"
                data-testid="placement-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Submit &amp; see my level{' '}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              {!allAnswered && (
                <p className="text-xs text-gray-400 text-center">
                  Answer every question to submit.
                </p>
              )}
            </div>
          )}

          {step === STEP.DONE && result && (
            <div className="space-y-5" data-testid="placement-result">
              <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-emerald-500/15 via-indigo-500/10 to-purple-500/15 border border-emerald-400/30">
                <Award className="w-10 h-10 mx-auto text-amber-300 mb-3" />
                <p className="text-sm text-gray-300">Your starting level</p>
                <p
                  className="text-4xl font-extrabold text-white mt-1"
                  data-testid="placement-level-label"
                >
                  {result.level_label}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Tier {result.level_tier} of 4 · +{result.coins_awarded}
                  <span className="text-amber-300"> coins</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Stat
                  label="Reading speed"
                  value={`${result.wpm}`}
                  sub="words / min"
                />
                <Stat
                  label="Comprehension"
                  value={`${result.comprehension_pct}%`}
                  sub={`${result.correct}/${result.total} correct`}
                />
              </div>

              {/* Recommended ebooks at the user's tier — concrete next-step
                  so they don't bounce back to a blank dashboard. */}
              {Array.isArray(result.recommended_books) && result.recommended_books.length > 0 && (
                <div className="space-y-2" data-testid="placement-recommendations">
                  <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                    Start with these — picked for {result.level_label}
                  </p>
                  <div className="space-y-2">
                    {result.recommended_books.map((b) => (
                      <button
                        key={b.content_id}
                        type="button"
                        onClick={() => {
                          if (typeof onClose === 'function') onClose();
                          navigate(`/reader/${b.content_id}`);
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-800/60 border border-slate-700 hover:border-indigo-400/60 hover:bg-slate-800 transition-colors text-left group"
                        data-testid={`placement-rec-${b.content_id}`}
                      >
                        {b.cover_image_url ? (
                          <img
                            src={b.cover_image_url}
                            alt={b.title}
                            className="w-12 h-16 rounded-md object-cover shrink-0 bg-slate-900"
                          />
                        ) : (
                          <div className="w-12 h-16 rounded-md bg-gradient-to-br from-indigo-500/30 to-purple-600/30 border border-indigo-400/30 flex items-center justify-center shrink-0">
                            <BookOpen className="w-5 h-5 text-indigo-200" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate group-hover:text-indigo-200">
                            {b.title}
                          </p>
                          <p className="text-[11px] text-gray-400 truncate">
                            {b.author || '—'}
                            {b.genre ? <> · {b.genre}</> : null}
                          </p>
                          <p className="text-[10px] uppercase tracking-wider text-indigo-300 mt-0.5">
                            {b.difficulty || '—'}
                            {b.word_count ? ` · ${b.word_count.toLocaleString()} words` : ''}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-indigo-300 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={onClose}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="placement-finish"
              >
                Start learning <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700 text-center">
    <p className="text-[10px] uppercase tracking-widest text-gray-400">{label}</p>
    <p className="text-2xl font-bold text-white mt-1">{value}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

export default PlacementTestModal;
